import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Phone, Mail, Clock, Linkedin, Twitter, Facebook, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0a1628] pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Contact Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tighter mb-6">
              Let's Build <span className="text-[#E63946]">Together</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-3xl">
              Have questions about our solutions? Want to discuss your warehouse infrastructure needs? Our team is ready to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 md:py-32 bg-[#0f1e32]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-8">
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Full Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input h-12 rounded-none"
                      placeholder="John Doe"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input h-12 rounded-none"
                      placeholder="john@company.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Phone Number</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input h-12 rounded-none"
                      placeholder="+91 98765 43210"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Subject *</label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="form-input h-12 rounded-none"
                      placeholder="Project Inquiry"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="form-input rounded-none resize-none"
                    placeholder="Tell us about your project..."
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary clip-slant h-14 px-10 text-base w-full md:w-auto"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-8">
                Contact Information
              </h2>

              <div className="space-y-8 mb-12">
                {/* Corporate Office */}
                <div className="industrial-card bg-[#0a1628] border border-white/10 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#E63946]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-white uppercase mb-2">Corporate Office</h3>
                      <p className="text-gray-400">
                        Gravique Pvt. Ltd.<br />
                        431, 4th Floor, Century Tower,<br />
                        H.B.Samaja Road, Bangalore 560004<br />
                        India
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="flex items-center gap-4 p-4 bg-[#0a1628] border border-white/10">
                  <Mail className="w-5 h-5 text-[#E63946]" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a href="mailto:info@warehausplus.com" className="text-white hover:text-[#E63946] transition-colors">
                      info@warehausplus.com
                    </a>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-center gap-4 p-4 bg-[#0a1628] border border-white/10">
                  <Clock className="w-5 h-5 text-[#E63946]" />
                  <div>
                    <p className="text-sm text-gray-500">Business Hours</p>
                    <p className="text-white">Monday - Saturday: 9:00 AM - 6:00 PM IST</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">Follow Us</h3>
                <div className="flex items-center gap-4">
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center border border-white/20 hover:border-[#E63946] hover:bg-[#E63946]/10 transition-colors"
                    data-testid="contact-linkedin"
                  >
                    <Linkedin className="w-5 h-5 text-gray-400 hover:text-[#E63946]" />
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center border border-white/20 hover:border-[#E63946] hover:bg-[#E63946]/10 transition-colors"
                    data-testid="contact-twitter"
                  >
                    <Twitter className="w-5 h-5 text-gray-400 hover:text-[#E63946]" />
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center border border-white/20 hover:border-[#E63946] hover:bg-[#E63946]/10 transition-colors"
                    data-testid="contact-facebook"
                  >
                    <Facebook className="w-5 h-5 text-gray-400 hover:text-[#E63946]" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 md:py-32" data-testid="map-section">
        <div className="container-custom">
          <div className="industrial-card bg-[#0f1e32] border border-white/10 p-2">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.0097680668!2d77.5921!3d12.9716!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae15c764f0c3e7%3A0x1c7d5e9e6c3b7b3e!2sCentury%20Tower%2C%20H.B.%20Samaja%20Road%2C%20Bangalore%20560004!5e0!3m2!1sen!2sin!4v1703000000000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Warehaus+ Location - Bangalore"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0f1e32]">
        <div className="container-custom">
          <div className="industrial-card gold-corners bg-[#0a1628] border border-[#E63946]/30 p-12 md:p-16 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
              Ready to <span className="text-[#E63946]">Transform Your Warehouse?</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
              Get in touch with our experts to discuss your warehouse infrastructure needs.
            </p>
            <a href="mailto:info@warehausplus.com" data-testid="contact-email-cta">
              <Button className="btn-gold clip-slant h-14 px-10 text-base">
                Email Us Today
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
